import React from 'react';
import { Link } from 'react-router-dom';

interface ToolCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  color: string;
}

const ToolCard: React.FC<ToolCardProps> = ({ title, description, icon, to, color }) => {
  return (
    <Link 
      to={to}
      className={`block p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 ${color} text-white`}
    >
      <div className="flex flex-col items-center text-center">
        <div className="p-3 rounded-full bg-white bg-opacity-20 mb-4">
          {icon}
        </div>
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="opacity-90">{description}</p>
      </div>
    </Link>
  );
};

const DesignerPanel: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Tasarımcı Paneli</h1>
        <p className="text-gray-600">Tüm tasarım araçlarına buradan erişebilirsiniz.</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Renkler */}
        <ToolCard
          title="Renkler"
          description="Renk paletleri ve renklerle ilgili tüm işlemler"
          to="/colors"
          color="bg-gradient-to-br from-pink-500 to-rose-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          }
        />
        
        {/* Kumaşlar */}
        <ToolCard
          title="Kumaşlar"
          description="Kumaş çeşitleri ve kumaş yönetimi"
          to="/fabrics"
          color="bg-gradient-to-br from-blue-500 to-cyan-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          }
        />
        
        {/* Aksesuarlar */}
        <ToolCard
          title="Aksesuarlar"
          description="Düğmeler, fermuarlar ve diğer aksesuarlar"
          to="/accessories"
          color="bg-gradient-to-br from-amber-500 to-yellow-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
        
        {/* Ölçüler */}
        <ToolCard
          title="Ölçüler"
          description="Standart beden ölçüleri ve kalıplar"
          to="/measurements"
          color="bg-gradient-to-br from-emerald-500 to-green-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
        />
        
        {/* Desenler */}
        <ToolCard
          title="Desenler"
          description="Kumaş desenleri ve baskı tasarımları"
          to="/patterns"
          color="bg-gradient-to-br from-purple-500 to-indigo-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
          }
        />
        
        {/* Prototipler */}
        <ToolCard
          title="Prototipler"
          description="Ürün prototipleri ve model tasarımları"
          to="/products?tab=prototypes"
          color="bg-gradient-to-br from-red-500 to-orange-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          }
        />
      </div>
    </div>
  );
};

export default DesignerPanel;
